/**
 * Admin UI Plugin Routes
 *
 * Defines all routes for the admin UI plugin.
 * Only accessible to platform admins.
 */

import { defineRoutes } from '@lognai/plugin-sdk';

/**
 * Plugin identifier
 */
export const pluginId = 'admin';

/**
 * All routes for the admin UI plugin
 */
export const routes = defineRoutes(pluginId, '/admin', [





  // User management
  {
    path: '/users',
    // Gated on the permission this page's own data calls require. It previously
    // carried only `requiredRoles`, which nothing reads — so the page, its nav
    // item and its forms rendered for anyone (QA ledger PERM-001).
    permissions: ['admin:user:read'],
    component: () =>
      import('./pages/UserManagementPage').then((m) => ({
        default: m.UserManagementPage,
      })),
    name: 'Users',
    description: 'Manage platform users',
    requiredRoles: ['platform_admin'],
    nav: {
      icon: { name: 'Users', size: 20 },
      showInNav: true,
      order: 2,
    },
  },

  // User role management
  {
    path: '/users/:userId/roles',
    // Gated on the permission this page's own data calls require. It previously
    // carried only `requiredRoles`, which nothing reads — so the page, its nav
    // item and its forms rendered for anyone (QA ledger PERM-001).
    permissions: ['admin:user:read'],
    component: () =>
      import('./pages/UserRoleManagementPage').then((m) => ({
        default: m.UserRoleManagementPage,
      })),
    name: 'User Roles',
    description: 'Manage user role assignments',
    requiredRoles: ['platform_admin'],
    parentPath: '/users',
    nav: {
      showInNav: false,
    },
  },

  // User direct-access management
  {
    path: '/users/:userId/access',
    // Gated on the permission this page's own data calls require. It previously
    // carried only `requiredRoles`, which nothing reads — so the page, its nav
    // item and its forms rendered for anyone (QA ledger PERM-001).
    permissions: ['admin:user:read'],
    component: () =>
      import('./pages/UserAccessManagementPage').then((m) => ({
        default: m.UserAccessManagementPage,
      })),
    name: 'User Access',
    description: 'Manage user direct-access assignments',
    requiredRoles: ['platform_admin'],
    parentPath: '/users',
    nav: {
      showInNav: false,
    },
  },

  // Role management — the COMPANY's own roles.
  //
  // `/admin/roles` used to live here and point at RoleManagementPage, which
  // reads the PLATFORM endpoints. Since this array is registered for every
  // authenticated user, that made a page every customer could open and none
  // could use: its three calls are gated on `admin:role:read`,
  // `admin:role:update` and `admin:access_object:read`, all PLATFORM-scoped
  // and ungrantable to a company role. The platform page now lives in
  // `platformRoutes` where it belongs, and this is the tenant's own surface.
  {
    path: '/roles',
    // Gated on the permission this page's own data calls require. It previously
    // carried only `requiredRoles`, which nothing reads — so the page, its nav
    // item and its forms rendered for anyone (QA ledger PERM-001).
    permissions: ['company:role:read', 'admin:role:read'],
    component: () =>
      import('./pages/CompanyRoleManagementPage').then((m) => ({
        default: m.CompanyRoleManagementPage,
      })),
    name: 'Roles & Permissions',
    description: "Manage your company's roles and what each one can do",
    nav: {
      icon: { name: 'Shield', size: 20 },
      showInNav: true,
      order: 3,
    },
  },


  // Partner Network management
  {
    path: '/partner-networks',
    // Gated on the permission this page's own data calls require. It previously
    // carried only `requiredRoles`, which nothing reads — so the page, its nav
    // item and its forms rendered for anyone (QA ledger PERM-001).
    permissions: ['admin:partner_network:read'],
    component: () =>
      import('./pages/PartnerNetworkManagementPage').then((m) => ({
        default: m.PartnerNetworkManagementPage,
      })),
    name: 'Partner Networks',
    description: 'Manage partner relationships',
    requiredRoles: ['platform_admin'],
    nav: {
      icon: { name: 'Network', size: 20 },
      showInNav: true,
      order: 5,
    },
  },

  // Partner Type management
  {
    path: '/partner-types',
    // Gated on the permission this page's own data calls require. It previously
    // carried only `requiredRoles`, which nothing reads — so the page, its nav
    // item and its forms rendered for anyone (QA ledger PERM-001).
    permissions: ['admin:partner_network:read'],
    component: () =>
      import('./pages/PartnerTypeManagementPage').then((m) => ({
        default: m.PartnerTypeManagementPage,
      })),
    name: 'Partner Types',
    description: 'Manage partner type definitions',
    requiredRoles: ['platform_admin'],
    parentPath: '/partner-networks',
    nav: {
      showInNav: false,
    },
  },

  // Feature flags live on the company record, in the company-detail
  // "Feature Flags" tab beside Plugins — they are per-company overrides, and
  // that tab is already platform-admin gated. There is deliberately no
  // top-level nav entry: `requiredRoles` is inert (never read by the route
  // registry), so a nav item here would be visible to every authenticated
  // user regardless of role.

  // Task assignment policy settings
  {
    path: '/task-assignment-policy',
    component: () =>
      import('./pages/TaskAssignmentPolicyPage').then((m) => ({
        default: m.TaskAssignmentPolicyPage,
      })),
    name: 'Task Assignment Policy',
    description: 'Control which roles can assign tasks to which roles',
    permissions: ['admin:company:update'],
    nav: {
      icon: { name: 'ShieldCheck', size: 20 },
      showInNav: true,
      order: 9,
    },
  },

  // Access tags (record scope): the company's tags and each user's values.
  // The endpoints act on the caller's own company only.
  {
    path: '/access-tags',
    component: () =>
      import('./pages/AccessTagsPage').then((m) => ({
        default: m.AccessTagsPage,
      })),
    name: 'Access tags',
    description: 'Limit who sees which shipments, purchase orders and workflow requests',
    permissions: ['admin:scope:manage'],
    nav: {
      icon: { name: 'Key', size: 20 },
      showInNav: true,
      order: 10,
    },
  },

  // Email notifications: which Freight RFQ / Shipment emails are sent and to whom.
  // The endpoints act on the caller's own company.
  {
    path: '/email-notifications',
    component: () =>
      import('./pages/EmailNotificationsPage').then((m) => ({
        default: m.EmailNotificationsPage,
      })),
    name: 'Email notifications',
    description: 'Choose which emails are sent and who receives them',
    permissions: ['admin:company:update'],
    nav: {
      icon: { name: 'Mail', size: 20 },
      showInNav: true,
      order: 12,
    },
  },

  // Custom fields: the company's extra fields and the record types each is used on.
  // The endpoints act on the caller's own company; platform admins use a company's page.
  {
    path: '/custom-fields',
    component: () =>
      import('./pages/CustomFieldsPage').then((m) => ({
        default: m.CustomFieldsPage,
      })),
    name: 'Custom fields',
    description: 'Extra fields on shipments, orders, RFQs, workflow requests and invoices',
    permissions: ['admin:custom_field:manage'],
    nav: {
      icon: { name: 'ListPlus', size: 20 },
      showInNav: true,
      order: 11,
    },
  },

  // ============================================================================
  // Company-scoped routes (from company-ui plugin - permission-based access)
  // ============================================================================

  // Company members / roles / invitations / hierarchy are now in-page tabs on
  // the company-detail page (?tab=members etc.), not standalone routes. Only the
  // create/edit sub-pages reached from within those tabs remain as routes below.

  // (Removed S5: the company Roles tab edits via the shared RoleEditModal —
  // the RoleDetailPage route was a second, field-subset editor over the same
  // records; its page file was deleted with it.)

  // Company member detail
  {
    path: '/companies/:companyId/members/:memberId',
    // Sub-page of /companies; gated the same way (QA ledger PERM-001).
    permissions: ['admin:company:read'],
    component: () =>
      import('./pages/MemberDetailPage').then((m) => ({
        default: m.MemberDetailPage,
      })),
    name: 'Member Details',
    description: 'View and edit member',
    parentPath: '/companies/:companyId',
    nav: {
      showInNav: false,
    },
  },

  // Create invitation (reached from the company's Invitations tab)
  {
    path: '/companies/:companyId/invitations/new',
    // Sub-page of /companies; gated the same way (QA ledger PERM-001).
    permissions: ['admin:company:update'],
    component: () =>
      import('./pages/InvitationCreatePage').then((m) => ({
        default: m.InvitationCreatePage,
      })),
    name: 'Invite Member',
    description: 'Send an invitation to join the company',
    parentPath: '/companies/:companyId',
    nav: {
      showInNav: false,
    },
  },

  // NOTE: per-company partnership routes removed — partnerships are managed
  // centrally on the Partner Networks page (/admin/partner-networks).

  {
    path: '/companies',
    // Gated on the permission this page's own data calls require. It previously
    // carried only `requiredRoles`, which nothing reads — so the page, its nav
    // item and its forms rendered for anyone (QA ledger PERM-001).
    permissions: ['admin:company:read'],
    component: () =>
      import('./pages/CompanyListPage').then((m) => ({
        default: m.CompanyListPage,
      })),
    name: 'Companies',
    description: 'Manage companies',
    requiredRoles: ['platform_admin'],
    nav: {
      icon: { name: 'Briefcase', size: 20 },
      showInNav: true,
      order: 1,
    },
  },

  {
    path: '/companies/hierarchy',
    // Gated on the permission this page's own data calls require. It previously
    // carried only `requiredRoles`, which nothing reads — so the page, its nav
    // item and its forms rendered for anyone (QA ledger PERM-001).
    permissions: ['admin:company:read'],
    component: () =>
      import('./pages/CompanyHierarchyPage').then((m) => ({
        default: m.CompanyHierarchyPage,
      })),
    name: 'Company Hierarchy',
    description: 'View company organizational hierarchy',
    requiredRoles: ['platform_admin'],
    parentPath: '/companies',
    nav: {
      showInNav: false,
    },
  },

  {
    path: '/companies/new',
    // Sub-page of /companies; gated the same way (QA ledger PERM-001).
    permissions: ['admin:company:create', 'company:company:create'],
    component: () =>
      import('./pages/CompanyCreatePage').then((m) => ({
        default: m.CompanyCreatePage,
      })),
    name: 'Create Company',
    description: 'Create a new company',
    requiredRoles: ['platform_admin'],
    parentPath: '/companies',
    nav: {
      showInNav: false,
    },
  },

  {
    path: '/companies/:companyId',
    // Sub-page of /companies; gated the same way (QA ledger PERM-001).
    permissions: ['admin:company:read'],
    component: () =>
      import('./pages/CompanyDetailsPage').then((m) => ({
        default: m.CompanyDetailsPage,
      })),
    name: 'Company Details',
    description: 'View and edit company details',
    requiredRoles: ['platform_admin'],
    parentPath: '/companies',
    nav: {
      showInNav: false,
    },
  },
]);

/**
 * Public routes (no auth required)
 * For accepting/declining invitations via token
 */
/**
 * Platform-only routes: the tenant/platform surfaces, not a company's own admin.
 *
 * Only two pages qualify, and the bar is "shows data no tenant should see":
 *
 *  - the platform dashboard, whose counts are aggregated with
 *    `skipTenantFilter: true` and span every tenant;
 *  - the access-object catalogue, which is the platform-wide permission
 *    registry rather than any company's data.
 *
 * The COMPANY pages are deliberately NOT here. `GET /api/companies` scopes a
 * tenant admin to their own company plus the ones they own (verified: 24 rows,
 * no other tenant), and tenant admins are explicitly allowed to create PARTNER
 * companies — see create-company-handler.ts. Hiding those pages broke partner
 * onboarding, and broke the member/invitation pages that navigate into them.
 *
 * They are registered CONDITIONALLY (see register.ts) rather than declared with
 * `permissions`, for two reasons:
 *
 *  1. `requiredRoles` on a route is inert — nothing in the route registry or the
 *     nav builder reads it. These routes carried it and were visible to everyone.
 *  2. No permission string can gate them either: a company `admin` role holds the
 *     `*` wildcard, which satisfies every permission check by design.
 *
 * Not registering them at all closes both the nav item and the direct URL, which
 * a permission gate alone could not.
 */
export const platformRoutes = defineRoutes(pluginId, '/admin', [
  {
    path: '/',
    component: () =>
      import('./pages/AdminDashboard').then((m) => ({
        default: m.AdminDashboard,
      })),
    name: 'Admin',
    description: 'Platform administration dashboard',
    requiredRoles: ['platform_admin'],
    nav: {
      icon: { name: 'Settings', size: 20 },
      showInNav: true,
      order: 100, // Show last in navigation
    },
  },

  {
    path: '/access-objects',
    component: () =>
      import('./pages/AccessObjectManagementPage').then((m) => ({
        default: m.AccessObjectManagementPage,
      })),
    name: 'Access Objects',
    description: 'Manage access objects and permissions',
    requiredRoles: ['platform_admin'],
    nav: {
      icon: { name: 'Key', size: 20 },
      showInNav: true,
      order: 4,
    },
  },

  // Roles across every company. Belongs here by the same bar as the two
  // above — it reads `/api/admin/roles` and the platform-wide access-object
  // registry, neither of which a tenant may touch. It is `/admin/all-roles`
  // rather than `/admin/roles` because that path is now the company's own
  // Roles & Permissions page, registered for everyone.
  {
    path: '/all-roles',
    component: () =>
      import('./pages/RoleManagementPage').then((m) => ({
        default: m.RoleManagementPage,
      })),
    name: 'All Roles',
    description: 'Manage roles across every company',
    requiredRoles: ['platform_admin'],
    nav: {
      icon: { name: 'Shield', size: 20 },
      showInNav: true,
      order: 3,
    },
  },
]);

export const publicRoutes = defineRoutes(pluginId, '/invite', [
  {
    path: '/accept',
    component: () =>
      import('./pages/InvitationAcceptPage').then((m) => ({
        default: m.InvitationAcceptPage,
      })),
    name: 'Accept Invitation',
    description: 'Accept an invitation to join a company',
    requiresAuth: false,
    nav: {
      showInNav: false,
    },
  },
]);
